/**
 * StatsFormatter - Pure utility functions for stat display and formatting
 *
 * These functions delegate to StatCalculator when available for caching benefits,
 * with fallback to direct calculation for backwards compatibility.
 */

/**
 * Get display name for a stat type
 * @param {string} statType - The internal stat type name
 * @returns {string} Human-readable display name
 */
export function getStatDisplayName(statType) {
    const statNames = {
        'baseSpeed': 'Idle Speed',
        'acceleration': 'Acceleration',
        'maxSpeed': 'Max Speed',
        'cruiseDuration': 'Autopilot Duration',
        'cruiseCooldown': 'Autopilot Cooldown',
        'momentum': 'Momentum Cap',
        'momentumRate': 'Momentum Rate',
        // Item-only stats (can also be boosted by driver skills)
        'salvageBonus': 'Scrap Bonus',
        'waveLuck': 'Discovery Bonus',
        'fuelTank': 'Fuel Tank',
        'itemUpgradeBonus': 'Fusion Bonus',
        // Milestone reward stats
        'mileMultiplier': 'Mile Multiplier'
    };
    return statNames[statType] || statType;
}

/**
 * Get unit for a stat type
 * @param {string} statType - The internal stat type name
 * @returns {string} Unit string (e.g., 'MPH', 's', '%') or empty string if none
 */
export function getStatUnit(statType) {
    const statUnits = {
        'baseSpeed': 'MPH',
        'acceleration': 'MPH/click',
        'maxSpeed': 'MPH',
        'cruiseDuration': 's',
        'cruiseCooldown': 's',
        'momentum': 'MPH',
        'momentumRate': 'MPH/s',
        // Percentage-based stats (displayed as %)
        'salvageBonus': '',  // Already shown as percentage
        'waveLuck': '',      // Already shown as percentage
        'mileMultiplier': '', // Already shown as percentage
        'fuelTank': '',      // Shown as duration (MIN/HR)
        'itemUpgradeBonus': '' // Already shown as percentage
    };
    return statUnits[statType] ?? '';
}

/**
 * Check if a stat is displayed as a percentage
 * @param {string} statType - The internal stat type name
 * @returns {boolean} True if stat should be displayed as percentage
 */
export function isPercentageStat(statType) {
    return ['salvageBonus'].includes(statType);
}

/**
 * Check if a stat is displayed as a multiplier (1.00x format)
 * @param {string} statType - The internal stat type name
 * @returns {boolean} True if stat should be displayed as multiplier
 */
export function isMultiplierStat(statType) {
    return ['mileMultiplier', 'waveLuck', 'itemUpgradeBonus'].includes(statType);
}

/**
 * Check if a stat is displayed as a duration (fuel tank, cruise duration)
 * @param {string} statType - The internal stat type name
 * @returns {boolean} True if stat should be displayed as duration
 */
export function isDurationStat(statType) {
    return statType === 'fuelTank' || statType === 'cruiseDuration';
}

/**
 * Format a duration value for display
 * @param {number} seconds - Duration in seconds
 * @returns {string} Formatted duration string (e.g., "5H 6M 30s")
 */
export function formatDurationValue(seconds) {
    if (seconds <= 0) return '0s';
    if (seconds < 60) return `${Math.round(seconds)}s`;
    if (seconds < 3600) {
        const mins = Math.floor(seconds / 60);
        const secs = Math.round(seconds % 60);
        return secs > 0 ? `${mins}M ${secs}s` : `${mins}M`;
    }
    const hours = Math.floor(seconds / 3600);
    const mins = Math.round((seconds % 3600) / 60);
    return mins > 0 ? `${hours}H ${mins}M` : `${hours}H`;
}

/**
 * Get next tier name needed to unlock a specific upgrade level
 * @param {number} level - The upgrade level
 * @returns {string} Tier name
 */
export function getNextTierForCap(level) {
    // Tier caps: faded=3, standard=5, pristine=7, golden=9, vectorized=12
    if (level <= 3) return 'Faded';
    if (level <= 5) return 'Standard';
    if (level <= 7) return 'Pristine';
    if (level <= 9) return 'Golden';
    return 'Vectorized';
}

/**
 * Get wave tier based on pack quality
 * @param {number} packQuality - Quality value 0.0 to 1.0
 * @returns {{name: string, color: string}} Tier info
 */
export function getWaveTier(packQuality) {
    if (packQuality >= 0.8) {
        return { name: 'Vectorized', color: 'rgba(230, 204, 128, 0.9)' };
    } else if (packQuality >= 0.6) {
        return { name: 'Golden', color: 'rgba(255, 140, 60, 0.9)' };
    } else if (packQuality >= 0.4) {
        return { name: 'Pristine', color: 'rgba(160, 100, 220, 0.9)' };
    } else if (packQuality >= 0.2) {
        return { name: 'Standard', color: 'rgba(100, 160, 255, 0.9)' };
    } else {
        return { name: 'Faded', color: 'rgba(150, 150, 150, 0.8)' };
    }
}

/**
 * Get detailed stat breakdown for tooltip
 * Delegates to StatCalculator if available for caching benefits
 * @param {string} type - Stat type
 * @param {number} level - Current upgrade level
 * @param {object} carConfig - Car configuration
 * @param {object} gameState - GameState instance
 * @returns {object} Breakdown object with all stat components
 */
export function getStatBreakdown(type, level, carConfig, gameState) {
    // Use StatCalculator if available (provides caching)
    if (gameState.statCalculator) {
        return gameState.statCalculator.getBreakdown(type, level, carConfig);
    }

    // Fallback: calculate directly (no caching)
    return _calculateBreakdownDirect(type, level, carConfig, gameState);
}

/**
 * Direct breakdown calculation (fallback when StatCalculator not available)
 */
function _calculateBreakdownDirect(type, level, carConfig, gameState) {
    const ab = gameState.getAscensionBonus(type);
    const tierMultiplier = gameState.getTierStatMultiplier();
    const prestigeBonus = gameState.state.prestige?.skillBonuses?.[type] || 0;
    const prestigeMultiplier = 1 + prestigeBonus;

    // No item effects in fallback mode (StatCalculator not available)
    const itemEffects = { add: 0, multiply: 1 };

    let baseValue, upgradeBonus, unit, nextLevelBonus;
    const maxLevel = gameState.getTierUpgradeCap();

    if (type === 'baseSpeed') {
        const increment = (carConfig.maxBaseSpeed - carConfig.baseSpeed) / 10;
        baseValue = carConfig.baseSpeed;
        upgradeBonus = level * increment;
        nextLevelBonus = increment;
        unit = 'MPH';
    } else if (type === 'acceleration') {
        const increment = (carConfig.maxAcceleration - carConfig.baseAcceleration) / 10;
        baseValue = carConfig.baseAcceleration;
        upgradeBonus = level * increment;
        nextLevelBonus = increment;
        unit = 'MPH/click';
    } else if (type === 'maxSpeed') {
        const increment = carConfig.maxSpeed / 10;
        baseValue = carConfig.maxSpeed;
        upgradeBonus = level * increment;
        nextLevelBonus = increment;
        unit = 'MPH';
    } else if (type === 'cruiseDuration') {
        const increment = (carConfig.maxCruiseDuration - carConfig.baseCruiseDuration) / 10;
        baseValue = carConfig.baseCruiseDuration;
        upgradeBonus = level * increment;
        nextLevelBonus = increment;
        unit = 's';
    } else if (type === 'cruiseCooldown') {
        const decrement = (carConfig.baseCruiseCooldown - 10) / 10;
        baseValue = carConfig.baseCruiseCooldown;
        upgradeBonus = -(level * decrement);
        nextLevelBonus = -decrement;
        unit = 's';
    } else if (type === 'momentum') {
        const increment = (carConfig.maxMomentumCap - carConfig.baseMomentumCap) / 10;
        baseValue = carConfig.baseMomentumCap;
        upgradeBonus = level * increment;
        nextLevelBonus = increment;
        unit = 'MPH';
    } else if (type === 'momentumRate') {
        const baseRate = carConfig.baseMomentumRate || 0.05;
        const maxRate = carConfig.maxMomentumRate || 0.10;
        const increment = (maxRate - baseRate) / 10;
        baseValue = baseRate;
        upgradeBonus = level * increment;
        nextLevelBonus = increment;
        unit = 'MPH/s';
    }

    const beforeTier = baseValue + upgradeBonus;
    // Tier multiplier applies to speed-based stats (MPH or MPH/s units)
    // It does NOT apply to time-based stats (cruiseDuration, cruiseCooldown)
    const isTimeBased = type === 'cruiseDuration' || type === 'cruiseCooldown';
    const tierBonus = isTimeBased ? 0 : beforeTier * (tierMultiplier - 1);
    const afterTier = beforeTier + tierBonus;

    // Add Ascension Bonus BEFORE prestige (matches GameState calculation order)
    const afterTierWithAB = afterTier + ab;

    // Calculate prestige bonus and subtotal based on stat type
    let prestigeBonusValue, subtotal;
    if (type === 'cruiseCooldown') {
        // Cooldown: multiply by (1 - prestigeBonus) to REDUCE cooldown
        // e.g., 10% bonus means 90% of original cooldown
        const prestigeReduction = 1 - prestigeBonus;
        prestigeBonusValue = afterTierWithAB - (afterTierWithAB * prestigeReduction);
        subtotal = afterTierWithAB * prestigeReduction;
    } else {
        // All other stats: multiply by (1 + prestigeBonus) to INCREASE
        prestigeBonusValue = afterTierWithAB * prestigeBonus;
        subtotal = afterTierWithAB * prestigeMultiplier;
    }

    const itemAddBonus = itemEffects.add;
    const afterItemAdd = subtotal + itemAddBonus;
    const itemMultiplyBonus = afterItemAdd * (itemEffects.multiply - 1);
    const total = afterItemAdd * itemEffects.multiply;

    return {
        baseValue,
        upgradeBonus,
        tierBonus,
        tierMultiplier,
        prestigeBonusValue,
        prestigeBonus,
        ab,
        itemAddBonus,
        itemMultiplyBonus,
        itemMultiplier: itemEffects.multiply,
        total,
        unit,
        nextLevelBonus: level < maxLevel ? nextLevelBonus : null,
        level,
        maxLevel
    };
}

/**
 * Get simple current stat value for display
 * @param {string} type - Stat type
 * @param {number} level - Current upgrade level
 * @param {object} carConfig - Car configuration
 * @param {object} gameState - GameState instance
 * @returns {string} Formatted stat value
 */
export function getCurrentStatValue(type, level, carConfig, gameState) {
    // Use StatCalculator if available
    if (gameState.statCalculator) {
        return gameState.statCalculator.getFormattedValue(type, level, carConfig);
    }

    // Fallback
    const breakdown = getStatBreakdown(type, level, carConfig, gameState);
    const value = breakdown.total;

    // Time-based stats show with 1 decimal for star differentiation
    if (type === 'cruiseDuration' || type === 'cruiseCooldown') {
        const formatted = value.toFixed(1);
        // Show (MIN) indicator when cooldown is at the 5s floor
        if (type === 'cruiseCooldown' && value <= 5) {
            return `${formatted}${breakdown.unit} (MIN)`;
        }
        return `${formatted}${breakdown.unit}`;
    } else if (type === 'momentum') {
        // Momentum shows with + prefix and one decimal
        return `+${value.toFixed(1)} ${breakdown.unit}`;
    } else if (type === 'momentumRate') {
        // Momentum rate shows with + prefix and three decimals (small values)
        return `+${value.toFixed(3)} ${breakdown.unit}`;
    } else {
        // All other stats show one decimal place for consistency
        return `${value.toFixed(1)} ${breakdown.unit}`;
    }
}

/**
 * Check if stat has any bonuses applied (includes item effects!)
 * @param {string} type - Stat type
 * @param {object} gameState - GameState instance
 * @returns {boolean} True if stat has bonuses
 */
export function hasStatBonuses(type, gameState) {
    // Use StatCalculator if available (includes item effects)
    if (gameState.statCalculator) {
        return gameState.statCalculator.hasModifiers(type);
    }

    // Fallback: check basic bonuses only (no item effects without StatCalculator)
    const ab = gameState.getAscensionBonus(type);
    const prestigeBonus = gameState.state.prestige?.skillBonuses?.[type] || 0;
    const tierMultiplier = gameState.getTierStatMultiplier();

    return ab !== 0 || prestigeBonus > 0 || tierMultiplier > 1;
}

/**
 * Get upgrade stats text (legacy alias for getCurrentStatValue)
 * @param {string} type - Stat type
 * @param {number} level - Current upgrade level
 * @param {object} carConfig - Car configuration
 * @param {object} gameState - GameState instance
 * @returns {string} Formatted stat value
 */
export function getUpgradeStatsText(type, level, carConfig, gameState) {
    return getCurrentStatValue(type, level, carConfig, gameState);
}

/**
 * Format a number with appropriate precision
 * @param {number} value - Value to format
 * @param {number} [decimals=1] - Decimal places
 * @returns {string} Formatted number
 */
export function formatStatNumber(value, decimals = 1) {
    if (value >= 100) {
        return Math.round(value).toString();
    }
    return value.toFixed(decimals);
}

/**
 * Format miles with K/M suffix for large numbers
 * @param {number} miles - Miles value
 * @returns {string} Formatted miles string
 */
export function formatMiles(miles) {
    if (miles >= 1000000) {
        return `${(miles / 1000000).toFixed(1)}M`;
    } else if (miles >= 1000) {
        return `${(miles / 1000).toFixed(1)}K`;
    }
    return miles.toString();
}

/**
 * Format time duration
 * @param {number} seconds - Duration in seconds
 * @returns {string} Formatted duration
 */
export function formatDuration(seconds) {
    if (seconds >= 60) {
        const mins = Math.floor(seconds / 60);
        const secs = Math.round(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    }
    return `${Math.round(seconds)}s`;
}
